import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type OfferStatus = 'draft' | 'offered' | 'accepted' | 'rejected';
export const OFFER_STATUSES: OfferStatus[] = ['draft', 'offered', 'accepted', 'rejected'];

export interface OfferStatusEntry {
  status: OfferStatus;
  reason: string | null;
  at: string;
  by_name: string | null;
}

/** Ein gespeichertes Angebot des Preisrechners (al-user-management, Migration 076). */
export interface PricingOffer {
  id: string;
  name: string;
  customer: string | null;
  status: OfferStatus;
  status_reason: string | null;
  status_history: OfferStatusEntry[];
  hidden: boolean;
  position: number;
  /** Eingaben des Rechners ohne `id`/`name` (Felder: pricing-model.ts `Scenario`). */
  scenario: Record<string, unknown>;
  created_by_name: string | null;
  updated_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface PricingSetting {
  value: Record<string, unknown>;
  updated_by_name: string | null;
  updated_at: string | null;
}

export interface PricingOffersState {
  offers: PricingOffer[];
  settings: Record<string, PricingSetting>;
}

export interface PricingOfferPatch {
  name?: string;
  customer?: string;
  scenario?: Record<string, unknown>;
  hidden?: boolean;
  position?: number;
  status_reason?: string;
  /** Stand, auf dem die Änderung beruht — sonst 409 mit dem aktuellen Angebot. */
  expected_updated_at?: string;
}

/** Angebote & Preise (super_admin): für alle super_admins gleich gespeichert. */
@Injectable({ providedIn: 'root' })
export class PricingOfferService {
  private readonly base = `${environment.userBackend}/admin/pricing`;

  constructor(private http: HttpClient) {}

  list(): Observable<PricingOffersState> {
    return this.http.get<{ data: PricingOffersState }>(`${this.base}/offers`).pipe(map((r) => r.data));
  }

  create(body: {
    name: string;
    customer?: string | null;
    scenario: Record<string, unknown>;
    hidden?: boolean;
  }): Observable<PricingOffer> {
    return this.http.post<{ data: PricingOffer }>(`${this.base}/offers`, body).pipe(map((r) => r.data));
  }

  update(id: string, patch: PricingOfferPatch): Observable<PricingOffer> {
    return this.http.patch<{ data: PricingOffer }>(`${this.base}/offers/${id}`, patch).pipe(map((r) => r.data));
  }

  setStatus(id: string, status: OfferStatus, reason: string): Observable<PricingOffer> {
    return this.http
      .post<{ data: PricingOffer }>(`${this.base}/offers/${id}/status`, { status, reason })
      .pipe(map((r) => r.data));
  }

  remove(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/offers/${id}`);
  }

  putSetting(key: 'costs', value: Record<string, unknown>): Observable<unknown> {
    return this.http.put(`${this.base}/settings/${key}`, { value });
  }
}
